import { useMemo } from "react";
import { useWagerProfile } from "@tvg/custom-hooks";
import { get, orderBy } from "lodash";
import type { TopPool } from "@tvg/ts-types/Pool";
import { useQuery, QueryHookOptions } from "@apollo/client";
import { ApolloOptionsProps, TopPoolsApolloResponse } from "../types";
import apolloOptions from "../graphql/options";
import { GET_TOP_POOLS } from "../graphql/queries";

export const removeFalsePinned = (topPools: TopPool[]) =>
  topPools.map<TopPool>((pool) =>
    get(pool, "race.highlighted.pinnedOrder", 0) === 0
      ? ({
          ...pool,
          race: {
            ...pool.race,
            highlighted: {
              ...pool.race.highlighted,
              pinnedOrder: null as unknown
            }
          }
        } as TopPool)
      : pool
  );

const getPools = (topPools: TopPool[]) =>
  orderBy(
    removeFalsePinned(topPools),
    [(pool) => get(pool, "race.highlighted.pinnedOrder", null), "amount"],
    ["asc", "desc"]
  );

const useTopPools = ({
  isEnabled = true,
  shouldUpdate = true,
  fetchPolicy = "cache-and-network",
  ...options
}: {
  isEnabled?: boolean;
  shouldUpdate?: boolean;
} & QueryHookOptions) => {
  const wagerProfile = useWagerProfile() as ApolloOptionsProps["wagerProfile"];

  // QUERY
  const { data, loading } = useQuery<TopPoolsApolloResponse>(
    GET_TOP_POOLS,
    isEnabled
      ? apolloOptions({ shouldUpdate, wagerProfile, fetchPolicy, ...options })
      : { skip: true }
  );

  const topPools: TopPool[] = useMemo<TopPool[]>(
    () => (isEnabled ? getPools(data?.carryOverPools ?? []) : []),
    [isEnabled, data?.carryOverPools]
  );

  return { topPools, loading, hasPools: isEnabled && topPools?.length > 0 };
};

export default useTopPools;
